import React, { useState, useEffect } from "react";
import { useAllProducts } from "../../services/admin/AllProductsServices";
import {
    Search,
    ShoppingBag,
    Music2,
    Box,
    LayoutGrid,
    List,
    Loader2
} from "lucide-react";



export default function AllProductsPage() {
    const { loading, fetchAllProducts } = useAllProducts();
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("table"); // 'table' or 'grid'

    useEffect(() => {
        const loadProducts = async () => {
            const data = await fetchAllProducts();
            setProducts(data);
        };
        loadProducts();
    }, [fetchAllProducts]);

    const filteredProducts = (products || []).filter(item => {
        const name = item.name || "";
        const category = item.category_name || "";
        const type = item.category_type || "";
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.toLowerCase().includes(searchTerm.toLowerCase()) ||
            type.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Inventory Overview ({filteredProducts.length} Items)
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            A consolidated view of all your music and merchandise products from the database.
                        </p>
                    </div>
                </div>

                {/* Filters & Controls */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative flex-1 w-full max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search all products..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm placeholder-black/70 dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="flex border border-black/5 rounded-xl overflow-hidden bg-[#FAFAFA] dark:bg-zinc-800">
                            <button
                                onClick={() => setViewMode("table")}
                                className={`p-2.5 transition-colors ${viewMode === "table" ? "bg-black text-white" : "text-black dark:text-white/40 hover:text-black dark:hover:text-white"}`}
                            >
                                <List size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-black text-white" : "text-black dark:text-white/40 hover:text-black dark:hover:text-white"}`}
                            >
                                <LayoutGrid size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Products Content */}
                {loading ? (
                    <div className="py-20 flex flex-col items-center justify-center">
                        <Loader2 className="animate-spin text-jazz-brown mb-4" size={40} />
                        <p className="text-black dark:text-white/50 animate-pulse font-medium">Loading inventory...</p>
                    </div>
                ) : viewMode === "table" ? (
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-black/5 dark:border-white/5 bg-[#FAFAFA] dark:bg-[#222]">
                                        <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Product</th>
                                        <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Type</th>
                                        <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Category</th>
                                        <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Price</th>
                                        <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Stock</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-black/5 dark:divide-white/5">
                                    {filteredProducts.map((product) => (
                                        <tr key={product.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-zinc-800 overflow-hidden flex items-center justify-center text-black/10">
                                                        {product.image ? (
                                                            <img
                                                                src={product.image}
                                                                alt={product.name}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'block';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div style={{ display: product.image ? 'none' : 'block' }}>
                                                            {product.category_type === "Music Content" ? <Music2 size={20} /> : <ShoppingBag size={20} />}
                                                        </div>
                                                    </div>
                                                    <span className="font-semibold text-sm text-black dark:text-white">{product.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded ${product.category_type === "Music Content" ? "text-blue-500 bg-blue-50 dark:bg-blue-900/10" : "text-purple-500 bg-purple-50 dark:bg-purple-900/10"
                                                    }`}>
                                                    {product.category_type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-black dark:text-white/60">
                                                {product.category_name}
                                            </td>
                                            <td className="px-6 py-4 font-bold text-sm">
                                                ${parseFloat(product.price).toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`text-xs font-bold ${product.stock_quantity === 0 ? "text-red-500" : product.stock_quantity < 15 ? "text-amber-500" : "text-black dark:text-white/40"}`}>
                                                    {product.stock_quantity} in stock
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredProducts.map((product) => (
                            <div key={product.id} className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 p-5 shadow-sm hover:shadow-xl hover:shadow-black/5 transition-all flex flex-col">
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center ${product.category_type === "Music Content" ? "bg-blue-50 text-blue-500" : "bg-purple-50 text-purple-500"
                                        }`}>
                                        {product.image ? (
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                            />
                                        ) : null}
                                        <div style={{ display: product.image ? 'none' : 'block' }}>
                                            {product.category_type === "Music Content" ? <Music2 size={24} /> : <ShoppingBag size={24} />}
                                        </div>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest ${product.status === 'active' ? "bg-green-500 text-white" : "bg-black text-white"
                                        }`}>
                                        {product.status}
                                    </span>
                                </div>
                                <h3 className="font-bold text-black dark:text-white mb-2 line-clamp-1" style={{ fontFamily: "Playfair Display, serif" }}>
                                    {product.name}
                                </h3>
                                <p className="text-xs text-black dark:text-white/30 font-bold uppercase tracking-widest mb-4">
                                    {product.category_name}
                                </p>
                                <div className="mt-auto flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/5">
                                    <span className="text-lg font-bold text-black dark:text-white">${parseFloat(product.price).toFixed(2)}</span>
                                    <span className={`text-xs font-bold tracking-widest ${product.stock_quantity === 0 ? "text-red-500" : "text-black dark:text-white/40"}`}>
                                        {product.stock_quantity} STOCK
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && filteredProducts.length === 0 && (
                    <div className="py-20 text-center">
                        <Box size={48} className="text-black/5 mx-auto mb-4" />
                        <p className="text-black dark:text-white/40 font-medium italic">No products found.</p>
                    </div>
                )}
            </div>
    );
}
